import legal from "./legal-en";

const en = {
  nav: { about: "Company", services: "Services", approach: "Approach", sectors: "Sectors", contact: "Contact", cta: "Get in touch", menu: "Menu", close: "Close", lang: "Language" },
  hero: {
    eyebrow: "GridJob OÜ — Tallinn, Estonia",
    lines: ["Events.", "Skills.", "Business.", "One grid."],
    sub: "An Estonian company that produces events, develops professional training and builds the business connections that keep industries moving — across borders.",
    cta: "Start a conversation",
    cta2: "What we do",
    caption: "Live production · Vocational training · Business services",
    meta: ["Est. 2026", "Tallinn, EE", "EN · ET · IT"],
    scroll: "Scroll",
  },
  marquee: ["Event production", "Professional training", "Training pathways", "Business development", "Business services", "Partnerships", "Stakeholder engagement", "Supplier coordination", "Digital communication"],
  about: {
    label: "01 — Company",
    title: "We are the connection point between businesses, professionals, training providers, associations and event organisers.",
    body: [
      "GridJob OÜ is a private limited company registered in Tallinn, Estonia, in 2026. We work in event production and coordination, professional training, business development and services to businesses.",
      "Our work spans the development of international projects, partnership building, stakeholder engagement, the coordination of suppliers and professionals, digital communication and the introduction of practical training and operational tools for the labour market. We facilitate transnational initiatives and help organisations turn plans into delivered results.",
    ],
    facts: [
      { k: "Legal form", v: "Private limited company (OÜ)" },
      { k: "Registered office", v: "Tallinn, Estonia" },
      { k: "Established", v: "2026" },
      { k: "Working languages", v: "English · Estonian · Italian" },
    ],
    imgAlt: "Tallinn old town skyline at sunset seen from the sea",
  },
  services: {
    label: "02 — Services",
    title: "Four disciplines. One coordinated team.",
    intro: "We combine production know-how, training design and business support so that projects, people and suppliers work as one system.",
    items: [
      { title: "Event production & coordination", desc: "From concept to show day: we plan, produce and coordinate events, venues, technical suppliers and on-site teams.", points: ["Production planning & budgeting", "Venue, crew & technical supplier coordination", "Demonstration events, workshops & showcases"], img: "/img/crowd.jpg", alt: "Concert audience under stage lights" },
      { title: "Professional training & pathways", desc: "We design and organise practical, outcome-based training that connects learners with real professional settings.", points: ["Course design & learning outcomes", "Trainer coordination & learner support", "Localisation and quality control of materials"] },
      { title: "Business development", desc: "We help organisations grow their reach, find the right partners and enter new markets in Estonia and abroad.", points: ["Partnership building & stakeholder mapping", "International project development", "Market entry support in the Baltic region"] },
      { title: "Business services", desc: "Operational support that keeps projects running: coordination, communication and reliable follow-through.", points: ["Supplier & professional coordination", "Digital communication & dissemination", "Data collection, reporting & evaluation"], img: "/img/team.jpg", alt: "Team working around a conference table" },
    ],
  },
  approach: {
    label: "03 — Approach",
    title: "How we work",
    steps: [
      { title: "Listen & map", desc: "We start from real needs: we map stakeholders, skills gaps and constraints before proposing anything." },
      { title: "Connect", desc: "We bring the right businesses, professionals, venues and training providers to the same table." },
      { title: "Coordinate", desc: "We run logistics, suppliers, schedules and communication so partners can focus on content." },
      { title: "Measure & sustain", desc: "We collect data, evaluate results and plan for adoption so the work outlives the project." },
    ],
  },
  network: {
    label: "04 — Network",
    title: "Who we connect",
    intro: "GridJob sits at the centre of a working network. Every connection is built around a concrete need of the labour market.",
    items: [
      { title: "Businesses", desc: "Employers, studios, venues and service companies looking for skilled people and reliable partners." },
      { title: "Professionals", desc: "Freelancers, technicians and specialists who want to grow, retrain or reach new clients." },
      { title: "Training providers", desc: "Schools, academies and training centres that need real-world settings and industry input." },
      { title: "Associations", desc: "Professional and sector associations that represent and support their members." },
      { title: "Event organisers", desc: "Festivals, promoters and producers who need coordinated teams and suppliers." },
    ],
  },
  sectors: {
    label: "05 — Sectors",
    title: "Where we work",
    items: [
      { title: "Music & live sound", desc: "Concert production, live audio services and the professionals behind them.", img: "/img/mixer.jpg", alt: "Illuminated faders of a live sound mixing desk" },
      { title: "Audiovisual & broadcast", desc: "Video, broadcast and audiovisual production companies and crews.", img: "/img/broadcast.jpg", alt: "Camera operator silhouetted against coloured studio lights" },
      { title: "Events & festivals", desc: "Conferences, festivals, showcases and cultural events of every size.", img: "/img/crowd.jpg", alt: "Festival crowd in front of a lit stage" },
      { title: "Education & training", desc: "Vocational and adult education providers, trainers and tutors.", img: "/img/workshop.jpg", alt: "Adults collaborating in a training workshop" },
      { title: "Enterprises & employers", desc: "Small and medium-sized companies building teams and partnerships.", img: "/img/team.jpg", alt: "Business team meeting around a table" },
    ],
  },
  principles: {
    label: "06 — Principles",
    title: "How we hold ourselves accountable",
    items: [
      { title: "Data protection by design", desc: "We process personal data lawfully and minimally, in line with the GDPR and the Estonian Personal Data Protection Act." },
      { title: "Equal opportunities", desc: "We design activities to be accessible and welcoming, regardless of gender, background, age or disability." },
      { title: "Integrity & transparency", desc: "Clear agreements, no conflicts of interest and honest reporting to every partner and client." },
      { title: "Quality & follow-through", desc: "Defined responsibilities, measurable results and documented quality control at every step." },
    ],
  },
  contact: {
    label: "07 — Contact",
    title: "Let's build something that works.",
    intro: "Tell us about your event, training idea or partnership. We reply to every enquiry, usually within a few working days.",
    fields: { name: "Full name", email: "Email", org: "Organisation (optional)", topic: "Topic", message: "Your message" },
    topics: { events: "Event production", training: "Professional training", business: "Business development", services: "Business services", partnership: "Partnership", other: "Other" },
    consent: "I have read the",
    consentLink: "Privacy Policy",
    consentEnd: "and agree that GridJob OÜ processes my data to answer this enquiry.",
    submit: "Send message",
    sending: "Sending…",
    success: "Thank you — your message has been received.",
    error: "Your message could not be sent. Please try again.",
    required: "Please fill in all required fields and accept the Privacy Policy.",
    details: { address: "Registered office", email: "Email", phone: "Phone", registry: "Registry code" },
  },
  footer: {
    tagline: "Events, skills and business — connected.",
    company: "Company details",
    legal: "Legal",
    rights: "All rights reserved.",
    admin: "Staff login",
    photos: "Photography: Unsplash (Unsplash License).",
  },
  cookie: { text: "This website only uses strictly necessary storage (language preference and secure staff sign-in). No tracking or advertising cookies.", link: "Cookie Policy", ok: "OK" },
  legalNav: { imprint: "Legal notice", privacy: "Privacy Policy", cookies: "Cookie Policy", accessibility: "Accessibility", terms: "Terms of use", back: "Back to home", updated: "Last updated" },
  legal,
};

export default en;
